import { SpoilerText } from '@/components/SpoilerText';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Star, ThumbsUp, MessageSquare, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { validateClean } from '@/lib/profanity';
import { UserModerationMenu } from '@/components/UserModerationMenu';
import { requestTicket } from "@/lib/guest";

interface Review {
  id: string;
  user_id: string;
  review_text: string;
  rating: number;
  spoiler: boolean;
  helpful_count: number;
  created_at: string;
  user_name?: string;
}

interface UserReviewsProps {
  tmdbId: number;
  mediaType: 'movie' | 'tv';
  title: string;
}

export const UserReviews = ({ tmdbId, mediaType, title }: UserReviewsProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [myReview, setMyReview] = useState<Review | null>(null);
  const [loading, setLoading] = useState(true);
  const [isWriting, setIsWriting] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [rating, setRating] = useState(0);
  const [spoiler, setSpoiler] = useState(false);
  const [helpfulIds, setHelpfulIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!user?.id || reviews.length === 0) return;
    supabase.from('review_votes').select('review_id').eq('user_id', user.id).in('review_id', reviews.map((r) => r.id))
      .then(({ data }) => setHelpfulIds(new Set((data ?? []).map((v) => v.review_id))));
  }, [user?.id, reviews]);

  const toggleHelpful = async (review: Review) => {
    if (!user) { requestTicket(); return; }
    const { data, error } = await supabase.rpc('toggle_review_helpful' as never, { p_review_id: review.id } as never);
    if (error) { toast({ title: 'Could not save your vote', variant: 'destructive' }); return; }
    const res = data as unknown as { voted: boolean; count: number };
    setHelpfulIds((prev) => { const n = new Set(prev); if (res.voted) n.add(review.id); else n.delete(review.id); return n; });
    setReviews((prev) => prev.map((r) => (r.id === review.id ? { ...r, helpful_count: res.count } : r)));
  };

  useEffect(() => {
    loadReviews();
  }, [tmdbId, mediaType, user]);

  const loadReviews = async () => {
    try {
      // Load all reviews
      const { data: reviewsData, error } = await supabase
        .from('user_reviews')
        .select('*')
        .eq('tmdb_id', tmdbId)
        .eq('media_type', mediaType)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      // Get unique user IDs
      const userIds = [...new Set(reviewsData?.map(r => r.user_id) || [])];
      
      // Fetch profiles for these users
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('user_id, full_name')
        .in('user_id', userIds);

      // Create a map of user_id to profile
      const profilesMap = new Map(
        profilesData?.map(p => [p.user_id, p]) || []
      );

      // Merge reviews with profile data
      const allReviews = (reviewsData || []).map(review => ({
        ...review,
        user_name: profilesMap.get(review.user_id)?.full_name || 'Anonymous'
      }));
      
      // Separate my review from others
      if (user) {
        const mine = allReviews.find(r => r.user_id === user.id);
        const others = allReviews.filter(r => r.user_id !== user.id);
        
        setMyReview(mine || null);
        setReviews(others);
        
        if (mine) {
          setReviewText(mine.review_text);
          setRating(mine.rating || 0);
          setSpoiler(mine.spoiler);
        }
      } else {
        setReviews(allReviews);
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!user) {
      requestTicket("write a review");
      return;
    }

    if (!reviewText.trim() || rating === 0) {
      toast({
        title: "Incomplete review",
        description: "Please provide both a rating and review text",
        variant: "destructive"
      });
      return;
    }

    const txtErr = validateClean(reviewText, "Review");
    if (txtErr) { toast({ title: txtErr, variant: "destructive" }); return; }

    try {
      const { error } = await supabase
        .from('user_reviews')
        .upsert({
          user_id: user.id,
          tmdb_id: tmdbId,
          media_type: mediaType,
          review_text: reviewText.trim(),
          rating,
          spoiler,
          is_public: true,
        });

      if (error) throw error;

      toast({
        title: "Review posted!",
        description: "Your review has been shared with the community"
      });

      setIsWriting(false);
      await loadReviews();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: "Error",
        description: "Failed to post review. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteReview = async () => {
    if (!user || !myReview) return;

    try {
      const { error } = await supabase
        .from('user_reviews')
        .delete()
        .eq('id', myReview.id);

      if (error) throw error;

      toast({
        title: "Review deleted",
        description: "Your review has been removed"
      });

      setMyReview(null);
      setReviewText('');
      setRating(0);
      setSpoiler(false);
      await loadReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
      toast({
        title: "Error",
        description: "Failed to delete review",
        variant: "destructive"
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          User Reviews
          {reviews.length + (myReview ? 1 : 0) > 0 && (
            <Badge variant="secondary">{reviews.length + (myReview ? 1 : 0)}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Write Review Section */}
        {user && !myReview && (
          <div className="space-y-3">
            {!isWriting ? (
              <Button onClick={() => setIsWriting(true)} className="w-full">
                Write a Review
              </Button>
            ) : (
              <div className="space-y-3 border rounded-lg p-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Your Rating</label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((star) => (
                      <button
                        key={star}
                        onClick={() => setRating(star)}
                        className="transition-all"
                      >
                        <Star
                          className={`h-5 w-5 ${
                            star <= rating
                              ? 'fill-primary text-primary'
                              : 'text-muted-foreground'
                          }`}
                        />
                      </button>
                    ))}
                    <span className="ml-2 text-sm font-medium">{rating}/10</span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Your Review</label>
                  <Textarea
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    placeholder={`Share your thoughts about ${title}...`}
                    rows={5}
                    maxLength={2000}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {reviewText.length}/2000 characters
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="spoiler"
                    checked={spoiler}
                    onChange={(e) => setSpoiler(e.target.checked)}
                    className="rounded"
                  />
                  <label htmlFor="spoiler" className="text-sm cursor-pointer">
                    Contains spoilers
                  </label>
                </div>

                <div className="flex gap-2">
                  <Button onClick={handleSubmitReview}>Post Review</Button>
                  <Button variant="outline" onClick={() => setIsWriting(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* My Review */}
        {myReview && (
          <div className="border-2 border-primary/20 rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="default">Your Review</Badge>
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 fill-primary text-primary" />
                  <span className="font-medium">{myReview.rating}/10</span>
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={handleDeleteReview}>
                Delete
              </Button>
            </div>

            {myReview.spoiler && (
              <Badge variant="destructive" className="flex items-center gap-1 w-fit">
                <AlertCircle className="h-3 w-3" />
                Contains Spoilers
              </Badge>
            )}

            <SpoilerText text={myReview.review_text} spoiler={myReview.spoiler} className="text-sm leading-relaxed" />
            <p className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(myReview.created_at), { addSuffix: true })}
            </p>
          </div>
        )}

        {/* Community Reviews */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse space-y-2 border rounded-lg p-4">
                <div className="h-4 bg-muted rounded w-1/4"></div>
                <div className="h-16 bg-muted rounded"></div>
              </div>
            ))}
          </div>
        ) : reviews.length > 0 ? (
          <div className="space-y-4">
            {reviews.map((review) => (
              <div key={review.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">
                      {review.user_name}
                    </span>
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-primary text-primary" />
                      <span className="text-sm font-medium">{review.rating}/10</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
                    </span>
                    <UserModerationMenu targetUserId={review.user_id} contentType="review" contentId={review.id} contextLabel="this review" hideBlock />
                  </div>
                </div>

                {review.spoiler && (
                  <Badge variant="outline" className="flex items-center gap-1 w-fit">
                    <AlertCircle className="h-3 w-3" />
                    Spoilers
                  </Badge>
                )}

                <SpoilerText text={review.review_text} spoiler={review.spoiler} className="text-sm leading-relaxed" />

                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => toggleHelpful(review)}
                    disabled={review.user_id === user?.id}
                    title={review.user_id === user?.id ? "You can't vote on your own review" : undefined}
                    className={`flex items-center gap-1 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${helpfulIds.has(review.id) ? 'text-marquee-bulb' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    <ThumbsUp className="h-3 w-3" />
                    <span>Helpful ({review.helpful_count})</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No reviews yet. Be the first to share your thoughts!</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
